import type {
	CommandConstructor,
	CommandProperties,
	SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

export type BackgroundConversationAction = {
	key: string;
	actionId?: string;
	toolSource?: string;
	invocationId: string;
	humanMessageId?: string;
	resumptionAvailable?: boolean;
	data: SerializableValue;
};

export type BackgroundConversationActionsDescriptor = {
	messageId: string;
	action: BackgroundConversationAction;
	revision: string;
};

/** Read-only context for actions that must be reviewed together in Chat. */
export type BackgroundConversationActionsReview = {
	messageId: string;
	reply: BackgroundConversationAction;
};

export type BackgroundConversationActionsLookup =
	| { status: 'ready'; descriptor: BackgroundConversationActionsDescriptor }
	| { status: 'unavailable'; review?: BackgroundConversationActionsReview }
	| { status: 'loading' | 'resolved' };

export type BackgroundConversationActionResolution = {
	invocationId: string;
	resolution: 'confirmed' | 'cancelled';
};

export type BackgroundConversationActionsResolutionResult = {
	status: 'accepted' | 'failed' | 'stale';
};

export type GetBackgroundConversationActionsCommandProperties = CommandProperties & {
	sourceId: string;
	conversationId: string;
	/**
	 * Lets Chat re-establish a lost same-tab source association after a reload.
	 * It is intentionally optional so existing callers retain their local-only lookup.
	 */
	commentId?: string;
};

export const GetBackgroundConversationActionsCommand: CommandConstructor<
	GetBackgroundConversationActionsCommandProperties,
	BackgroundConversationActionsLookup | undefined
> = createCommand<
	GetBackgroundConversationActionsCommandProperties,
	BackgroundConversationActionsLookup | undefined
>('get-background-conversation-actions-command');

export type ResolveBackgroundConversationActionsCommandProperties = CommandProperties & {
	sourceId: string;
	conversationId: string;
	messageId: string;
	revision: string;
	resolution: BackgroundConversationActionResolution;
};

export const ResolveBackgroundConversationActionsCommand: CommandConstructor<
	ResolveBackgroundConversationActionsCommandProperties,
	BackgroundConversationActionsResolutionResult
> = createCommand<
	ResolveBackgroundConversationActionsCommandProperties,
	BackgroundConversationActionsResolutionResult
>('resolve-background-conversation-actions-command');
